import { Space, TableColumnsType, Tooltip, Typography } from 'antd';
import {
	PreworkGroup,
	Batch,
	PreworkCandidateSummary,
} from '../../shared/PreWorkServiceTypes';
import { useTranslation } from 'react-i18next';
import { usePreworkGroupId } from '../../hooks/usePreworkGroupId';
import { useCallback, useEffect, useState } from 'react';
import {
	PREWORK_ASSIGN_MENTOR,
	PREWORK_EDIT_GROUP,
	PREWORK_VIEW_GROUP,
} from '../../shared/Routes';
import { utc } from 'moment';
import { CandidateExaminationDetail } from '../../shared/ExamModuleTypes';
import { CalendarOutlined } from '@ant-design/icons';
import { DATE_DD_MM_YYYY } from '../../shared/Constants';
import { useSnapshot } from 'valtio';
import { CandidatesBatchState } from './CreateGroupPage/CandidatesList.store';
import { checkIfDateIsSameOrBefore, hasPrivilege } from '../../shared/Utils';
import useUserSession from '../../hooks/useUserSession';
import { PrivilegeCodes } from '../../shared/RoleConstants';

export const CandidateTableColumnsPreWork = (
	hideSorter?: boolean,
	viewCandidateMentor?: boolean
): TableColumnsType<PreworkCandidateSummary> => {
	const { t } = useTranslation();

	const COLUMN_STYLE = 'w-48';

	return [
		{
			title: t('candidateName'),
			dataIndex: ['applicant', 'firstName'],
			key: 'name',
			render: (value, record, index) => (
				<Typography.Text
					key={index}
					ellipsis={true}
					className={`truncate ${COLUMN_STYLE}`}
				>
					{value} {record.applicant.middleName} {record.applicant.lastName}
				</Typography.Text>
			),
		},
		{
			title: t('gender'),
			dataIndex: ['applicant', 'gender'],
			key: 'applicant.gender',
		},
		{
			title: t('cityFilterName'),
			dataIndex: ['applicant', 'city'],
			key: 'city',
			sorter: !hideSorter && {
				compare: (a, b) => {
					const cityA = a?.applicant?.city?.toLowerCase() || '';
					const cityB = b?.applicant?.city?.toLowerCase() || '';
					return cityA.localeCompare(cityB);
				},
				multiple: 1,
			},
		},
		{
			title: t('pincode'),
			dataIndex: ['applicant', 'pincode'],
			key: 'pincode',
			sorter: !hideSorter && {
				compare: (a, b) => {
					const pinCodeA = a?.applicant?.pincode?.toLowerCase() || '';
					const pinCodeB = b?.applicant?.pincode?.toLowerCase() || '';
					return pinCodeA.localeCompare(pinCodeB);
				},
				multiple: 2,
			},
		},
		{
			title: t('address'),
			dataIndex: 'applicant.addressLine1',
			key: 'address',
			render: (__, record, index) => {
				const address = `${record?.applicant.addressLine1 || ''} ${
					record?.applicant.addressLine2 || ''
				} ${record?.applicant.addressLine3 || ''} ${
					record?.applicant.addressLine4 || ''
				}`;
				const lines = address.split(' ');
				const halfIndex = Math.ceil(lines.length / 2);
				const firstHalf = lines.slice(0, halfIndex).join(' ');
				const secondHalf = lines.slice(halfIndex).join(' ');

				return (
					<Tooltip title={address}>
						<div>
							<Typography.Text
								key={`${index}-line1`}
								ellipsis={true}
								className={`truncate ${COLUMN_STYLE}`}
							>
								{firstHalf}
							</Typography.Text>
							<br />
							<Typography.Text
								key={`${index}-line2`}
								ellipsis={true}
								className={`truncate ${COLUMN_STYLE}`}
							>
								{secondHalf}
							</Typography.Text>
						</div>
					</Tooltip>
				);
			},
		},
		{
			title: t('state'),
			dataIndex: ['applicant', 'state'],
			key: 'state',
			sorter: !hideSorter && {
				compare: (a, b) => {
					const stateA = a?.applicant?.state?.toLowerCase() || '';
					const stateB = b?.applicant?.state?.toLowerCase() || '';
					return stateA.localeCompare(stateB);
				},
				multiple: 3,
			},
			render: (value, __, index) => (
				<Typography.Text key={index} ellipsis={true}>
					{value}
				</Typography.Text>
			),
		},
		{
			title: t('zone'),
			dataIndex: 'zone',
			key: 'zone',
			render: (__, record, index) => (
				<Tooltip title={record?.zone || record?.center?.zone}>
					<Typography.Text key={index} ellipsis={true}>
						{record?.zone || record?.center?.zone}
					</Typography.Text>
				</Tooltip>
			),
		},
		{
			title: t('mobile'),
			dataIndex: ['applicant', 'mobile'],
			key: 'applicant.mobile',
		},
		{
			title: t('email'),
			dataIndex: ['applicant', 'email'],
			key: 'applicant.email',
		},
		{
			title: t('abhyasiId'),
			dataIndex: ['applicant', 'srcmId'],
			key: 'abhyasiId',
			render: (__, record, index) => (
				<span key={index}>
					{record?.applicant.srcmId || record?.applicant.srcmid}
				</span>
			),
		},
		{
			title: t('country'),
			dataIndex: ['applicant', 'country'],
			key: 'country',
		},
		{
			title: t('languages'),
			dataIndex: 'preferredLanguageOfTraining',
			key: 'preferredLanguageOfTraining',
			render: (__, record, index) => (
				<span key={index}>
					{record?.preferredLanguageOfTraining
						? record.preferredLanguageOfTraining.charAt(0) +
						  record.preferredLanguageOfTraining.slice(1).toLowerCase()
						: '-'}
				</span>
			),
		},
		{
			title: t('preceptor'),
			dataIndex: 'respPrefectName',
			key: 'respPrefectName',
			render: (value, __, index) => (
				<Typography.Text key={index} ellipsis={true}>
					{value}
				</Typography.Text>
			),
		},
		...(viewCandidateMentor
			? [
					{
						title: t('mentor'),
						dataIndex: 'mentorName',
						key: 'mentorName',
						width: 200,
					},
			  ]
			: []),
	];
};

const getCandidatesNames = (
	candidateGroupMembers: Array<PreworkCandidateSummary>
) => {
	if (candidateGroupMembers) {
		return candidateGroupMembers
			.map((item) => {
				const { firstName, middleName, lastName } = item.applicant;
				return `${firstName || ''} ${middleName || ''} ${
					lastName || ''
				}`.trim();
			})
			.join(', ');
	}
};

export const GroupsTableColumns = (): TableColumnsType<PreworkGroup> => {
	const { t } = useTranslation();
	const { navigateWithPreworkGroupId } = usePreworkGroupId();
	const candidateBatchSnap = useSnapshot(CandidatesBatchState);
	const selectedBatchID = candidateBatchSnap?.selectedBatch?.id;
	sessionStorage.setItem(
		'preworkSelectedBatchID',
		JSON.stringify(selectedBatchID)
	);
	const [isResultPublished, setIsResultPublished] = useState<boolean>(false);

	const { loggedInUserPrivileges } = useUserSession();

	const showEdit = hasPrivilege(
		PrivilegeCodes.UPDATE_PREWORK_GROUP,
		loggedInUserPrivileges
	);

	const showAssignPreworkMentor = hasPrivilege(
		PrivilegeCodes.ASSIGN_PREWORK_MENTOR,
		loggedInUserPrivileges
	);

	const handleAssignMentorClick = useCallback(
		(groupId: number) => {
			navigateWithPreworkGroupId(PREWORK_ASSIGN_MENTOR, groupId);
		},
		[navigateWithPreworkGroupId]
	);

	const handleEditGroupClick = useCallback(
		(groupId: number) => {
			navigateWithPreworkGroupId(PREWORK_EDIT_GROUP, groupId);
		},
		[navigateWithPreworkGroupId]
	);

	const handleViewGroupClick = useCallback(
		(groupId: number) => {
			navigateWithPreworkGroupId(PREWORK_VIEW_GROUP, groupId);
		},
		[navigateWithPreworkGroupId]
	);

	useEffect(() => {
		const result = checkIfDateIsSameOrBefore(
			candidateBatchSnap?.selectedBatch?.snpResultDateForCandidate
		);
		setIsResultPublished(result);
	}, [candidateBatchSnap?.selectedBatch]);

	return [
		{
			title: t('groupId'),
			dataIndex: 'preworkGroupNumber',
			key: 'preworkGroupNumber',
		},
		{
			title: t('newCandidateNames'),
			dataIndex: 'preworkGroupMembers',
			key: 'preworkGroupMembers',
			render: (__, record, index) => (
				<span key={index}>
					{getCandidatesNames(record.preworkGroupMembers)}
				</span>
			),
		},
		{
			title: t('mentorName'),
			dataIndex: 'mentor',
			key: 'mentorName',
			render: (__, record, index) =>
				record.mentor ? (
					<span key={index}>
						{record.mentor.firstName} {record.mentor.middleName}{' '}
						{record.mentor.lastName}
					</span>
				) : (
					'-'
				),
		},
		{
			title: t('action'),
			key: 'operation',
			fixed: 'right',
			width: 150,
			render: (__, record) => (
				<Space size="middle" className="whitespace-nowrap">
					{showEdit && (
						<>
							<a
								className={`${
									isResultPublished
										? ' cursor-not-allowed text-gray-400 hover:text-gray-300'
										: 'underline hover:underline-offset-4'
								}`}
								onClick={() =>
									isResultPublished ? null : handleEditGroupClick(record.id)
								}
							>
								{t('editOrUpdate')}
							</a>
						</>
					)}
					<a
						className={`${
							isResultPublished
								? ' cursor-not-allowed text-gray-400 hover:text-gray-300'
								: 'underline hover:underline-offset-4'
						}`}
						onClick={() =>
							isResultPublished ? null : handleViewGroupClick(record.id)
						}
					>
						{t('view')}
					</a>

					{showAssignPreworkMentor && (
						<a
							className={`${
								isResultPublished
									? ' cursor-not-allowed text-gray-400 hover:text-gray-300'
									: 'underline hover:underline-offset-4'
							}`}
							onClick={() =>
								isResultPublished ? null : handleAssignMentorClick(record.id)
							}
						>
							{t('assignMentor')}
						</a>
					)}
				</Space>
			),
		},
	];
};

export const CandidateTableColumns = (
	hanldeHoldClick: (candidate: CandidateExaminationDetail) => void,
	hanldeEditClick: (candidate: CandidateExaminationDetail) => void,
	displayHold: boolean
): TableColumnsType<CandidateExaminationDetail> => {
	const { t } = useTranslation();

	const handleHoldCandidateClick = useCallback(
		(candidate: CandidateExaminationDetail) => {
			hanldeHoldClick(candidate);
		},
		[hanldeHoldClick]
	);

	const handleEditCandidateClick = useCallback(
		(candidate: CandidateExaminationDetail) => {
			hanldeEditClick(candidate);
		},
		[hanldeEditClick]
	);

	const candidateStatus = useCallback(
		(record: CandidateExaminationDetail) => {
			if (
				record.reasonForCancellingExam &&
				record.reasonForCancellingExam !== ''
			) {
				return t('cancelled');
			} else if (record.registrationStatus === 'REGISTERED') {
				return t('registered');
			} else if (record.registrationStatus === 'NOT_REGISTERED') {
				return t('notRegistered');
			}
		},
		[t]
	);

	return [
		{
			title: t('abhyasiId'),
			dataIndex: 'srcmId',
			key: 'abhyasiId',
			render: (__, record, index) => (
				<span key={index}>{record?.profile.srcmId}</span>
			),
		},
		{
			title: t('name'),
			dataIndex: 'profile.name',
			key: 'name',
			render: (__, record, index) => (
				<Tooltip title={record?.profile.name} placement="topLeft">
					<span key={index} className="truncate max-w-[200px] block">
						{record?.profile.name}
					</span>
				</Tooltip>
			),
		},
		{
			title: t('testCenter'),
			dataIndex: 'center',
			key: 'testCenter',
			render: (__, record, index) => (
				<span key={index}>{record?.examinationCenter.centerName}</span>
			),
		},
		{
			title: t('candidateCenter'),
			dataIndex: 'profile.center',
			key: 'candidateCenter',
			render: (__, record, index) => (
				<span key={index}>{record?.profile.center}</span>
			),
		},
		{
			title: t('examLanguage'),
			dataIndex: 'languages',
			key: 'languages',
			render: (__, record, index) => (
				<span key={index}>{record?.preferredLanguageForExamination}</span>
			),
		},
		{
			title: t('accommodation'),
			dataIndex: 'noOfAccommodations',
			key: 'noOfAccommodations',
			render: (__, record, index) => (
				<span key={index}>{record?.noOfAccommodations || 0}</span>
			),
		},
		{
			title: t('specialRequests'),
			dataIndex: 'specialRequest',
			key: 'specialRequest',
			render: (__, record, index) => (
				<Tooltip title={record?.specialRequest} placement="topLeft">
					<span key={index} className="truncate max-w-[200px] block">
						{record?.specialRequest}
					</span>
				</Tooltip>
			),
		},
		{
			title: t('admitCard'),
			dataIndex: 'admitCardNumber',
			key: 'admitCardNumber',
			render: (__, record, index) => (
				<span key={index}>{record?.admitCardNumber || '-'}</span>
			),
		},
		{
			title: t('attendance'),
			dataIndex: 'attendanceStatus',
			key: 'attendanceStatus',
			render: (__, record, index) => (
				<span key={index}>
					{record?.attendanceStatus
						? t(record.attendanceStatus.toLowerCase())
						: '-'}
				</span>
			),
		},
		{
			title: t('totalMarks'),
			dataIndex: 'totalMarksObtained',
			key: 'totalMarksObtained',
			render: (__, record, index) => (
				<span key={index}>
					{record.attendanceStatus === 'ABSENT'
						? 'NA'
						: record.resultUploaded
						? record?.totalMarksObtained || 0
						: '-'}
				</span>
			),
		},
		{
			title: t('percentage'),
			dataIndex: 'totalPercentage',
			key: 'totalPercentage',
			render: (__, record, index) => {
				const percent = record.totalPercentage && `${record.totalPercentage}%`;
				return (
					<span key={index}>
						{record.attendanceStatus === 'ABSENT'
							? 'NA'
							: record.resultUploaded
							? percent || '0%'
							: '-'}
					</span>
				);
			},
		},
		{
			title: t('status'),
			dataIndex: 'status',
			key: 'status',
			render: (__, record, index) => (
				<span key={index}>{candidateStatus(record)}</span>
			),
		},
		{
			title: t('reasonForCancellingExam'),
			dataIndex: 'reasonForCancellingExam',
			key: 'reasonForCancellingExam',
			width: 150,
			render: (__, record, index) => (
				<Tooltip title={record?.reasonForCancellingExam} placement="topLeft">
					<span key={index} className="truncate max-w-[200px] block">
						{record?.reasonForCancellingExam}
					</span>
				</Tooltip>
			),
		},
		{
			title: t('action'),
			key: 'operation',
			fixed: 'right',
			width: 150,
			render: (__, record) => {
				const showHoldLink =
					displayHold &&
					(!record.reasonForCancellingExam ||
						record.reasonForCancellingExam === '');

				return (
					<Space size="middle" className="whitespace-nowrap">
						<a
							className={`underline hover:underline-offset-4 ${
								showHoldLink ? '' : 'pointer-events-none text-gray'
							}`}
							onClick={() => handleHoldCandidateClick(record)}
						>
							{t('hold')}
						</a>
						<a
							className={`underline hover:underline-offset-4 ${
								showHoldLink ? '' : 'pointer-events-none text-gray'
							}`}
							onClick={() => handleEditCandidateClick(record)}
						>
							{t('editOrUpdate')}
						</a>
					</Space>
				);
			},
		},
	];
};

export const EligibleCandidateTableColumns =
	(): TableColumnsType<CandidateExaminationDetail> => {
		const { t } = useTranslation();

		return [
			{
				title: t('abhyasiId'),
				dataIndex: 'srcmId',
				key: 'abhyasiId',
				render: (__, record, index) => (
					<span key={index}>{record?.profile.srcmId}</span>
				),
			},
			{
				title: t('name'),
				dataIndex: 'profile.name',
				key: 'name',
				render: (__, record, index) => (
					<span key={index}>{record?.profile.name}</span>
				),
			},
			{
				title: t('candidateCenter'),
				dataIndex: 'profile.center',
				key: 'candidateCenter',
				render: (__, record, index) => (
					<span key={index}>{record?.profile.center}</span>
				),
			},
			{
				title: t('registrationStatus'),
				dataIndex: 'registrationStatus',
				key: 'registrationStatus',
				render: (__, record, index) => {
					if (record.registrationStatus === 'NOT_REGISTERED') {
						return <span key={index}>{t('notRegistered')}</span>;
					} else if (record.registrationStatus === 'REGISTERED') {
						return <span key={index}>{t('registered')}</span>;
					}
				},
			},
		];
	};

export const BatchTableColumns = (): TableColumnsType<Batch> => {
	const { t } = useTranslation();

	return [
		{
			title: t('batchName'),
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: t('startDate'),
			dataIndex: 'startDate',
			key: 'startDate',
			render: (__, record, index) => (
				<span key={index} className="flex items-center whitespace-nowrap">
					<CalendarOutlined />
					<span className="ml-2">
						{utc(record.startDate).format(DATE_DD_MM_YYYY)}
					</span>
				</span>
			),
		},
		{
			title: t('endDate'),
			dataIndex: 'endDate',
			key: 'endDate',
			render: (__, record, index) => (
				<span key={index} className="flex items-center whitespace-nowrap">
					<CalendarOutlined />
					<span className="ml-2">
						{utc(record.endDate).format(DATE_DD_MM_YYYY)}
					</span>
				</span>
			),
		},
	];
};
